import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { Button, Spinner, Form, InputGroup, Pagination } from "react-bootstrap";
import { FaPlus, FaTicketAlt, FaFolderOpen, FaHourglassHalf, FaCheckCircle, FaChevronLeft, FaChevronRight } from "react-icons/fa";
import { useQuery } from "@tanstack/react-query";

import { useAuth } from "../context/AuthContext";
import { getTickets, getTicketsAwaitingApproval } from "../api/TICKET/ticket";
import NewTicket from "../components/Modals/Create/NewTicket";
import TicketRow from "./Tickets/TicketRow";

const MOBILE_BREAKPOINT = 1000;
const DASHBOARD_STATE_KEY = "dashboard.myTickets.state.v1";
const STATUS_LABELS = {
  open: "Ouvert",
  in_progress: "En cours",
  closed: "Clôturé",
  approved: "Approuvé",
};
const SORTABLE_FIELDS = {
  id: "id",
  titre: "titre",
  dateDebut: "date_debut",
  client: "client",
};

const getPageNumbers = (current, total) => {
  if (total <= 7) return Array.from({ length: total }, (_, i) => i + 1);

  const delta = 2;
  const left = Math.max(2, current - delta);
  const right = Math.min(total - 1, current + delta);

  return [
    1,
    ...(left > 2 ? ["..."] : []),
    ...Array.from({ length: right - left + 1 }, (_, i) => left + i),
    ...(right < total - 1 ? ["..."] : []),
    total,
  ];
};

const formatTicket = (t) => ({
  id: t.id,
  dateDebut: t.start_date || t.created_at,
  client: t.client?.name || "Non assigné",
  clientId: t.client?.id,
  titre: t.title,
  intervenants: [
    ...(t.groups?.map(g => g.name) || []),
    ...(t.users?.map(u => `${u.name} ${u.surname}`) || [])
  ],
  statut: STATUS_LABELS[t.status] ?? t.status,
  rawStatus: t.status,
  interventions: t.ticket_interventions || [],
});

function KpiCard({ icon, color, label, value, loading, subtitle, onClick }) {
  return (
    <div
      className={`card border-left-${color} shadow h-100 py-2`}
      onClick={onClick}
      style={onClick ? { cursor: "pointer" } : {}}
    >
      <div className="card-body">
        <div className="row no-gutters align-items-center">
          <div className="col mr-2">
            <div className={`text-xs font-weight-bold text-${color} text-uppercase mb-1`}>
              {label}
            </div>
            <div className="h5 mb-0 font-weight-bold text-gray-800">
              {loading ? <Spinner animation="border" size="sm" /> : value}
            </div>
            {subtitle && <div className="text-xs text-muted mt-1">{subtitle}</div>}
          </div>
          <div className="col-auto" style={{ fontSize: "2rem", opacity: 0.25 }}>
            {icon}
          </div>
        </div>
      </div>
    </div>
  );
}

const Home = ({ titre }) => {
  const navigate = useNavigate();
  const { user, hasPermission } = useAuth();

  const [showNewTicket, setShowNewTicket] = useState(false);
  const [isMobile, setIsMobile] = useState(window.innerWidth < MOBILE_BREAKPOINT);
  const [search, setSearch] = useState("");
  const [limit, setLimit] = useState(10);
  const [page, setPage] = useState(1);
  const [sortField, setSortField] = useState(SORTABLE_FIELDS.dateDebut);
  const [sortOrder, setSortOrder] = useState("DESC");
  const [stateRestored, setStateRestored] = useState(false);

  const currentUserId = user?.data?.id;
  const currentUserGroupIds = Array.isArray(user?.data?.groups)
    ? user.data.groups.map((g) => g.id).filter(Boolean)
    : [];
  const canApprove = hasPermission("APPROVE_TICKET");

  const {
    data: myTicketsData,
    isLoading: ticketsLoading,
    isError: ticketsError,
    error: ticketsErrorMsg,
  } = useQuery({
    queryKey: ["dashboardMyTickets", page, limit, search, currentUserId, currentUserGroupIds.join(","), sortField, sortOrder],
    queryFn: () => getTickets(page, limit, search, {
      statuses: ["open", "in_progress"],
      userIds: currentUserId ? [currentUserId] : [],
      groupIds: currentUserGroupIds,
    }, sortField, sortOrder),
    enabled: !!currentUserId,
    staleTime: 60_000,
  });

  const { data: activeCountsData } = useQuery({
    queryKey: ["dashboardActiveCounts"],
    queryFn: () => getTickets(1, 50, "", { statuses: ["open", "in_progress"] }),
    staleTime: 60_000,
  });

  const { data: approvalData } = useQuery({
    queryKey: ["ticketsApproval"],
    queryFn: () => getTicketsAwaitingApproval(1, 1, ""),
    enabled: canApprove,
  });

  useEffect(() => {
    const handleResize = () => setIsMobile(window.innerWidth < MOBILE_BREAKPOINT);
    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, []);

  useEffect(() => {
    try {
      const raw = localStorage.getItem(DASHBOARD_STATE_KEY);
      if (!raw) {
        setStateRestored(true);
        return;
      }

      const saved = JSON.parse(raw);
      if (typeof saved.search === "string") setSearch(saved.search);
      if ([10, 25, 50].includes(saved.limit)) setLimit(saved.limit);
      if (Number.isInteger(saved.page) && saved.page > 0) setPage(saved.page);
      if (Object.values(SORTABLE_FIELDS).includes(saved.sortField)) setSortField(saved.sortField);
      if (saved.sortOrder === "ASC" || saved.sortOrder === "DESC") setSortOrder(saved.sortOrder);
    } catch {
      // Ignore invalid persisted state.
    } finally {
      setStateRestored(true);
    }
  }, []);

  const countedActiveTickets = Array.isArray(activeCountsData?.data?.tickets) ? activeCountsData.data.tickets : [];
  const openCount  = countedActiveTickets.filter(t => t.status === "open").length;
  const inProgCount = countedActiveTickets.filter(t => t.status === "in_progress").length;
  const approvalCount = approvalData?.data?.count ?? 0;

  const myTickets = Array.isArray(myTicketsData?.data?.tickets) ? myTicketsData.data.tickets : [];

  const myTicketsActiveCount = Number(myTicketsData?.data?.count || 0);
  const totalItems = Number(myTicketsData?.data?.count || 0);
  const totalPages = Math.max(1, Math.ceil(totalItems / limit));
  const safePage = Math.min(page, totalPages);
  const paginatedTickets = myTickets;

  const resetFilters = () => {
    setSearch("");
    setSortField(SORTABLE_FIELDS.dateDebut);
    setSortOrder("DESC");
    setPage(1);
  };

  const handleSort = (field) => {
    if (sortField === field) {
      setSortOrder((prev) => (prev === "ASC" ? "DESC" : "ASC"));
    } else {
      setSortField(field);
      setSortOrder("DESC");
    }
    setPage(1);
  };

  const paginationBar = (keyPrefix) => (
    <Pagination className="mb-0 flex-wrap">
      <Pagination.Prev
        onClick={() => setPage((p) => Math.max(1, p - 1))}
        disabled={safePage === 1}
      >
        {isMobile ? <FaChevronLeft /> : "Précédent"}
      </Pagination.Prev>

      {getPageNumbers(safePage, totalPages).map((p, idx) =>
        p === "..." ? (
          <Pagination.Ellipsis key={`${keyPrefix}-ellipsis-${idx}`} disabled />
        ) : (
          <Pagination.Item
            key={`${keyPrefix}-page-${p}`}
            active={p === safePage}
            onClick={() => setPage(p)}
          >
            {p}
          </Pagination.Item>
        )
      )}

      <Pagination.Next
        onClick={() => setPage((p) => Math.min(totalPages, p + 1))}
        disabled={safePage === totalPages}
      >
        {isMobile ? <FaChevronRight /> : "Suivant"}
      </Pagination.Next>
    </Pagination>
  );

  useEffect(() => {
    setPage(1);
  }, [limit]);

  useEffect(() => {
    if (!stateRestored) return;
    const payload = {
      search,
      limit,
      page,
      sortField,
      sortOrder,
    };
    localStorage.setItem(DASHBOARD_STATE_KEY, JSON.stringify(payload));
  }, [search, limit, page, sortField, sortOrder, stateRestored]);

  return (
    <>
      <div className="d-sm-flex align-items-center justify-content-between mb-4">
        <h1 className="h3 mb-0 text-gray-800">{titre}</h1>
        {hasPermission("CREATE_TICKET") && (
          <Button variant="warning" className="btn-icon-split" onClick={() => setShowNewTicket(true)}>
            <span className="icon text-white"><FaPlus /></span>
            <span className="text text-white">Ajouter un ticket</span>
          </Button>
        )}
      </div>

      <div className="row mb-4">
        <div className="col-xl-3 col-md-6 mb-4">
          <KpiCard icon={<FaTicketAlt />} color="warning" label="Mes tickets actifs" value={myTicketsActiveCount} loading={ticketsLoading} subtitle={`${myTickets.length} au total`} />
        </div>
        <div className="col-xl-3 col-md-6 mb-4">
          <KpiCard icon={<FaFolderOpen />} color="primary" label="Tickets ouverts" value={openCount} loading={ticketsLoading} onClick={() => navigate("/tickets")} />
        </div>
        <div className="col-xl-3 col-md-6 mb-4">
          <KpiCard icon={<FaHourglassHalf />} color="info" label="Tickets en cours" value={inProgCount} loading={ticketsLoading} onClick={() => navigate("/tickets")} />
        </div>
        <div className="col-xl-3 col-md-6 mb-4">
          <KpiCard icon={<FaCheckCircle />} color="success" label="En attente d'approbation" value={approvalCount} onClick={() => navigate("/ticket-attente-approbation")} />
        </div>
      </div>

      <div className="row">
        <div className="col-12">
          <div className="card border-left-warning shadow mb-4">
            <div className="card-header py-3 d-flex justify-content-between">
              <h6 className="m-0 font-weight-bold text-warning">Mes tickets</h6>
              <span className="text-muted small">{totalItems} résultat(s)</span>
            </div>

            <div className="card-body">
              <div className="d-flex flex-wrap align-items-center gap-2 mb-3">
                <div style={{ width: "84px" }}>
                  <Form.Select
                    value={limit}
                    onChange={(e) => setLimit(Number(e.target.value))}
                    disabled={ticketsLoading}
                  >
                    <option value={10}>10</option>
                    <option value={25}>25</option>
                    <option value={50}>50</option>
                  </Form.Select>
                </div>

                <div style={{ minWidth: "240px", flex: "1 1 320px" }}>
                  <InputGroup>
                    <Form.Control
                      type="text"
                      placeholder="Recherche par titre"
                      value={search}
                      onChange={(e) => {
                        setSearch(e.target.value);
                        setPage(1);
                      }}
                    />
                  </InputGroup>
                </div>

                <Button variant="outline-secondary" onClick={resetFilters}>Réinitialiser</Button>
              </div>

              {ticketsLoading ? (
                <div className="text-center py-4"><Spinner animation="border" /></div>
              ) : ticketsError ? (
                <div className="alert alert-danger mb-0">
                  <strong>Erreur:</strong> {ticketsErrorMsg?.message || "Impossible de charger vos tickets."}
                </div>
              ) : paginatedTickets.length === 0 ? (
                <div className="text-center text-muted py-4">Aucun ticket ne correspond aux filtres.</div>
              ) : isMobile ? (
                <div className="row g-3 p-3">
                  {paginatedTickets.map((t, idx) => (
                    <div key={t.id ?? idx} className="col-12">
                      <TicketRow ticket={formatTicket(t)} listState={{}} asCard />
                    </div>
                  ))}
                </div>
              ) : (
                <div className="table-responsive">
                  <table className="table table-striped table-hover align-middle mb-0">
                    <thead>
                      <tr>
                        {[
                          { label: "ID", field: SORTABLE_FIELDS.id },
                          { label: "Titre", field: SORTABLE_FIELDS.title },
                          { label: "Date début", field: SORTABLE_FIELDS.dateDebut },
                          { label: "Client", field: SORTABLE_FIELDS.client },
                        ].map(({ label, field }) => (
                          <th
                            key={field}
                            onClick={() => handleSort(field)}
                            style={{ cursor: "pointer", userSelect: "none", whiteSpace: "nowrap" }}
                          >
                            {label}{" "}
                            {sortField === field
                              ? (sortOrder === "ASC" ? "▲" : "▼")
                              : <span style={{ opacity: 0.25 }}>▲</span>}
                          </th>
                        ))}
                        <th>Intervenant(s)</th>
                        <th>Intervention(s)</th>
                        <th>Statut</th>
                        <th>Action(s)</th>
                      </tr>
                    </thead>
                    <tbody>
                      {paginatedTickets.map((t, idx) => (
                        <TicketRow key={t.id ?? idx} ticket={formatTicket(t)} listState={{}} />
                      ))}
                    </tbody>
                  </table>
                </div>
              )}

              {!ticketsLoading && !ticketsError && totalItems > 0 && (
                <div className="d-flex justify-content-end mt-3">
                  {paginationBar("my-tickets")}
                </div>
              )}
            </div>
          </div>
        </div>
      </div>

      {showNewTicket && (
        <NewTicket showNewTicket={showNewTicket} setShowNewTicket={setShowNewTicket} />
      )}
    </>
  );
};

export default Home;
